import type { FRVLevel, FRVStageStatus } from '../../utils/api'

export const LEVEL_STYLE: Record<FRVLevel, { label: string; cls: string }> = {
  V3: { label: 'Checked', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  V2: { label: 'Lowered', cls: 'bg-sky-50 text-sky-800 border-sky-200' },
  V1: { label: 'Replayed', cls: 'bg-violet-50 text-violet-800 border-violet-200' },
  V0: { label: 'Screened', cls: 'bg-slate-50 text-slate-700 border-slate-200' },
}

export function LevelBadge({ level }: { level: FRVLevel }) {
  const s = LEVEL_STYLE[level]
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold whitespace-nowrap ${s.cls}`}>
      <span className="font-mono">{level}</span> {s.label}
    </span>
  )
}

export const STAGE_STATUS: Record<FRVStageStatus, { label: string; dot: string; text: string; icon: string }> = {
  proved: { label: 'Proved in Lean', dot: 'bg-emerald-600 border-emerald-600', text: 'text-emerald-800', icon: '✓' },
  checked: { label: 'Checked by program', dot: 'bg-sky-600 border-sky-600', text: 'text-sky-800', icon: '✓' },
  partial: { label: 'Partly covered', dot: 'bg-amber-400 border-amber-500', text: 'text-amber-800', icon: '◐' },
  trusted: { label: 'Trusted input', dot: 'bg-white border-slate-400', text: 'text-slate-600', icon: '○' },
  open: { label: 'Open', dot: 'bg-white border-slate-300 border-dashed', text: 'text-slate-500', icon: '·' },
}

const STAGE_SHORT: Record<string, string> = {
  prog: 'Source', qppm: 'Logical', qec: 'QEC', sysprog: 'Schedule', vm: 'VM',
}

/** Five dots, one per pipeline stage, each with an accessible title. */
export function ChainDots({ chain }: { chain: { id: string; status: FRVStageStatus }[] }) {
  return (
    <div className="flex items-center gap-1" role="list" aria-label="Verification chain">
      {chain.map(s => (
        <span
          key={s.id}
          role="listitem"
          title={`${STAGE_SHORT[s.id] ?? s.id}: ${STAGE_STATUS[s.status].label}`}
          aria-label={`${STAGE_SHORT[s.id] ?? s.id}: ${STAGE_STATUS[s.status].label}`}
          className={`w-3 h-3 rounded-full border-2 ${STAGE_STATUS[s.status].dot}`}
        />
      ))}
    </div>
  )
}

export function ChainLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-qcloud-muted">
      <span className="font-semibold uppercase tracking-wider">Chain</span>
      <span>Source → Logical → QEC → Schedule → VM</span>
      {(['proved', 'checked', 'partial', 'open'] as FRVStageStatus[]).map(k => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className={`w-2.5 h-2.5 rounded-full border-2 ${STAGE_STATUS[k].dot}`} />
          {STAGE_STATUS[k].label}
        </span>
      ))}
    </div>
  )
}

/** Small warning-free tag for qualifications such as a non-default VM mode. */
export function Tag({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <span title={title} className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10.5px] font-medium whitespace-nowrap">
      {children}
    </span>
  )
}
